import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { getPlaceDetailsQueryKey } from './queryKeys';
import { useAppContext } from '../useAppContext';
import { useSessionToken } from '../stores/useSessionToken';
import { useSearchStore } from '../stores/search/useSearchStore';
import { combineBoundingBoxes } from '../../utils/map';

export const usePlaceDetails = ({ placeIds: placeIdsOverride }: { placeIds?: string[] } = {}) => {
  const {
    api,
    options: { language },
  } = useAppContext();
  const { refreshSessionToken, sessionToken } = useSessionToken();
  const searchPlaceIds = useSearchStore((state) => state.placeIds);
  const placeIds = placeIdsOverride ?? searchPlaceIds;
  const uniquePlaceIds = useMemo(() => [...new Set(placeIds ?? [])], [placeIds]);

  return useQueries({
    queries: uniquePlaceIds.map((placeId) => ({
      queryKey: getPlaceDetailsQueryKey({ placeId, language }),
      queryFn: async () => {
        const result = await api.getLocationDetails(placeId, {
          language,
          sessionToken,
        });

        if (result.status === 'error') {
          throw result.error;
        }

        refreshSessionToken();
        return result.data;
      },
    })),
    combine: (results) => {
      const placeDetailsList = results.flatMap((result) => (result.data ? [result.data] : []));
      const primaryPlaceDetails = placeDetailsList[0];

      return {
        data: primaryPlaceDetails,
        error: results.find((result) => result.error)?.error,
        isError: results.some((result) => result.isError),
        isFetching: results.some((result) => result.isFetching),
        isLoading: results.some((result) => result.isLoading),
        isPending: results.some((result) => result.isPending),
        isSuccess: results.every((result) => result.isSuccess),
        placeDetails: primaryPlaceDetails,
        placeDetailsList,
        primaryPlaceDetails,
        combinedBoundingBox: combineBoundingBoxes(
          placeDetailsList.flatMap((placeDetails) => (placeDetails.boundingBox ? [placeDetails.boundingBox] : [])),
        ),
        refetch: async () => Promise.all(results.map((result) => result.refetch())),
      };
    },
  });
};
